import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchSapErrors, type SapErrorItem } from "../api";
import { formatIban } from "../lib/grouping";

/**
 * Vista de AUDITORÍA: lista todas las cuentas bancarias activas cuyo último
 * sync SAP terminó con error, agrupadas por empresa. Útil tras un "Sync SAP"
 * masivo desde Resumen empresas para ver de un vistazo qué IBANs necesitan
 * atención (típicamente porque no están registrados en HouseBankAccounts de
 * SAP, o porque la CompanyDB configurada no es la correcta).
 */
export function SapErrors() {
  const [items, setItems] = useState<SapErrorItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetchSapErrors();
      setItems(r.items);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  // Agrupar por empresa preservando el orden que ya trae el backend.
  const grouped = new Map<string, SapErrorItem[]>();
  for (const it of items ?? []) {
    const key = it.companyName || it.companyId || "(sin empresa)";
    const arr = grouped.get(key);
    if (arr) arr.push(it);
    else grouped.set(key, [it]);
  }

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h2>Errores de sync SAP</h2>
          <p className="meta">
            Cuentas bancarias activas cuyo último sync SAP terminó con error.
            El motivo más habitual es que el IBAN no está registrado como
            cuenta bancaria propia en la CompanyDB configurada de su empresa
            (SAP Business One → Gestión bancaria → Cuentas bancarias propias).
          </p>
        </div>
        <button type="button" onClick={load} disabled={loading}>
          {loading ? "Cargando…" : "↻ Recargar"}
        </button>
      </div>

      {error && <p className="error">Error: {error}</p>}
      {!error && items === null && !loading && <p>Cargando…</p>}
      {items && items.length === 0 && (
        <p className="meta">
          ✅ Ninguna cuenta activa tiene error de sync SAP.
        </p>
      )}

      {items && items.length > 0 && (
        <>
          <p className="meta">
            <strong>{items.length}</strong> cuenta{items.length === 1 ? "" : "s"} con error, en{" "}
            <strong>{grouped.size}</strong> empresa{grouped.size === 1 ? "" : "s"}.
          </p>
          {Array.from(grouped.entries()).map(([companyName, rows]) => (
            <div key={companyName} style={{ marginTop: "1.2rem" }}>
              <h3 style={{ margin: "0 0 0.4rem 0" }}>🏢 {companyName}</h3>
              <table className="data">
                <thead>
                  <tr>
                    <th>IBAN</th>
                    <th>Banco</th>
                    <th>Alias</th>
                    <th>G/L SAP</th>
                    <th>Último intento</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.iban}>
                      <td>
                        <Link to={`/cuentas/${encodeURIComponent(r.iban)}/apuntes-sap`}>
                          <code>{formatIban(r.iban)}</code>
                        </Link>
                      </td>
                      <td>
                        <code>{r.bankCode}</code>
                        {r.bankName && (
                          <div className="meta" style={{ fontSize: "0.85em" }}>
                            {r.bankName}
                          </div>
                        )}
                      </td>
                      <td>{r.alias || <span className="meta">—</span>}</td>
                      <td>
                        {r.sapGLAccount ? (
                          <code>{r.sapGLAccount}</code>
                        ) : (
                          <span className="meta">—</span>
                        )}
                      </td>
                      <td>
                        {r.lastSyncedAt ? (
                          new Date(r.lastSyncedAt).toLocaleString()
                        ) : (
                          <span className="meta">—</span>
                        )}
                      </td>
                      <td>
                        <span className="error">{r.error}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </>
      )}
    </section>
  );
}
